import { TaskTemplateMatrixState } from '@/redux';
import { Table, TableColumnType } from '@/components';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import {
  ImportPathTemplateTaskFormValue,
  TemplateTaskToImportDto,
} from './ImportPathTemplateTasksModal';
import { useEffect, useMemo, useState } from 'react';
import { ResponsiblePersonReadControl } from '@/views/Tasks.Details/ImportTasks/Fields/ResponsiblePersonReadControl';

interface Props {
  name: string;
  data: TaskTemplateMatrixState[];
  isFetching: boolean;
  communityId: string | undefined;
}

export interface TaskTemplateTableDto extends TemplateTaskToImportDto {
  isGroup: boolean;
  groupName: string;
  children?: TaskTemplateTableDto[];
  formikIndex?: number;
  key: string;
}

function useColumns(communityId: string | undefined, name: string) {
  const { t } = useTranslation();

  return useMemo<TableColumnType<TaskTemplateTableDto>[]>(() => {
    const columns: Array<TableColumnType<TaskTemplateTableDto> | false> = [
      { key: 'checked', width: 50 },
      {
        key: 'groupName',
        width: 150,
        render: (_, row) => (row.isGroup ? row.groupName : ''),
      },
      {
        title: t('tasks.name'),
        key: 'name',
        render: (_, row) => row.name,
      },
      {
        title: t('tasks.responsiblePerson'),
        key: 'responsiblePerson',
        render: (_, row, index) =>
          row.responsiblePerson !== null &&
          row.responsiblePerson !== undefined &&
          row.formikIndex && (
            <ResponsiblePersonReadControl
              placeholder={t('tasks.responsiblePerson')}
              communityId={communityId}
              initialValue={row.responsiblePerson}
            />
          ),
      },
      {
        title: t('tasks.costEstimated'),
        key: 'costEstimate',
        render: (_, row) => row.costEstimate,
      },
    ];
    return columns.filter(Boolean) as TableColumnType<TaskTemplateTableDto>[];
  }, [communityId, t]);
}

export function ImportPathTemplateTasksTable({
  name,
  data,
  isFetching,
  communityId,
}: Props) {
  const { values, setFieldValue } =
    useFormikContext<ImportPathTemplateTaskFormValue>();

  const [selected, setSelected] = useState<string[]>([]);
  const tableData: TaskTemplateTableDto[] = useMemo(() => {
    const groups = data.map((template) => {
      const children: TaskTemplateTableDto[] = values.rows
        .map((row, index) => ({
          ...row,
          isGroup: false,
          groupName: template.name,
          formikIndex: index,
          checked: false,
          key: `${template.id}-${index}`,
        }))
        .filter((row) => row.groupId === template.id);
      return {
        isGroup: true,
        name: '',
        groupId: template.id,
        groupName: template.name,
        children,
        checked: false,
        key: template.id,
      };
    });

    return groups;
  }, [data, values.rows]);

  const columns = useColumns(communityId, name);

  useEffect(() => {
    const updated = values.rows.map((row, i) => ({
      ...row,
      checked: selected.includes(`${row.groupId}-${i}`),
    }));
    setFieldValue('rows', updated);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, setFieldValue]);

  console.log(selected);
  return (
    <Table<TaskTemplateTableDto>
      dataSource={tableData}
      rowSelection={{
        type: 'checkbox',
        onChange: (_, rows) => {
          setSelected(rows.map((x) => x.key));
        },
        selectedRowKeys: selected,
        fixed: 'left',
        checkStrictly: false,
      }}
      columns={columns}
      rowKey={(row: TaskTemplateTableDto) => row.key}
      pagination={false}
      loading={isFetching}
      scroll={{ y: 400 }}
      name={name}
      hideDefaultActions
    />
  );
}
